import React, { useState } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import {
  Download,
  FileSpreadsheet,
  Calendar,
  CreditCard,
  Home,
  Users,
  Loader2,
  CheckCircle,
  Filter,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const ExportCenter = () => {
  const [activeExport, setActiveExport] = useState('appointments');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    role: 'all'
  });
  const [lastExport, setLastExport] = useState(null);

  const downloadCSV = (data, filename, headers, rowMapper) => {
    if (!data || data.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }

    const csvContent = [
      headers.join(','),
      ...data.map(item => rowMapper(item).map(val => `"${String(val || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success(`${data.length} enregistrements exportés`);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      let response;
      let filename;
      let headers;
      let rowMapper;

      switch (activeExport) {
        case 'appointments':
          response = await adminAPI.exportAppointments({
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
            status: filters.status !== 'all' ? filters.status : undefined
          });
          filename = 'rendez_vous_dockhta';
          headers = ['Date', 'Heure', 'Patient', 'Professionnel', 'Type', 'Statut', 'Notes', 'Créé le'];
          rowMapper = (apt) => [
            apt.date,
            apt.time_slot,
            apt.patient_name,
            apt.professional_name,
            apt.appointment_type,
            apt.status,
            apt.notes,
            apt.created_at?.split('T')[0]
          ];
          break;

        case 'transactions':
          response = await adminAPI.exportTransactions({
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
            status: filters.status !== 'all' ? filters.status : undefined
          });
          filename = 'transactions_dockhta';
          headers = ['ID', 'Utilisateur', 'Montant (FCFA)', 'Type', 'Statut', 'Référence', 'Date'];
          rowMapper = (tx) => [
            tx.id,
            tx.user_name || tx.user_id,
            tx.amount,
            tx.type || 'payment',
            tx.status,
            tx.reference,
            tx.created_at?.split('T')[0]
          ];
          break;

        case 'homecare':
          response = await adminAPI.exportHomeCare({
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
            status: filters.status !== 'all' ? filters.status : undefined
          });
          filename = 'soins_domicile_dockhta';
          headers = ['Patient', 'Type de soin', 'Adresse', 'Date souhaitée', 'Statut', 'Professionnel', 'Créé le'];
          rowMapper = (req) => [
            req.patient_name,
            req.care_type,
            req.address,
            req.preferred_date,
            req.status,
            req.professional_name || '-',
            req.created_at?.split('T')[0]
          ];
          break;

        case 'users':
          response = await adminAPI.exportUsers(filters.role !== 'all' ? filters.role : null);
          filename = 'utilisateurs_dockhta';
          headers = ['Nom', 'Email', 'Téléphone', 'Rôle', 'Localisation', 'Inscription', 'Statut'];
          rowMapper = (u) => [
            u.full_name,
            u.email,
            u.phone,
            u.role,
            u.location,
            u.created_at?.split('T')[0],
            u.is_suspended ? 'Suspendu' : 'Actif'
          ];
          break;

        default:
          throw new Error('Type d\'export non supporté');
      }

      const data = response.data.data || [];
      setLastExport({
        type: activeExport,
        count: data.length,
        timestamp: new Date().toISOString()
      });
      
      downloadCSV(data, filename, headers, rowMapper);

    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export');
    } finally {
      setLoading(false);
    }
  };

  const exportTypes = [
    { id: 'appointments', label: 'Rendez-vous', icon: Calendar, color: 'purple' },
    { id: 'transactions', label: 'Transactions', icon: CreditCard, color: 'emerald' },
    { id: 'homecare', label: 'Soins à domicile', icon: Home, color: 'blue' },
    { id: 'users', label: 'Utilisateurs', icon: Users, color: 'slate' },
  ];

  const statusOptions = {
    appointments: [
      { value: 'all', label: 'Tous les statuts' },
      { value: 'pending', label: 'En attente' },
      { value: 'confirmed', label: 'Confirmés' },
      { value: 'completed', label: 'Terminés' },
      { value: 'cancelled', label: 'Annulés' },
    ],
    transactions: [
      { value: 'all', label: 'Tous les statuts' },
      { value: 'success', label: 'Réussis' },
      { value: 'pending', label: 'En attente' },
      { value: 'failed', label: 'Échoués' },
    ],
    homecare: [
      { value: 'all', label: 'Tous les statuts' },
      { value: 'pending', label: 'En attente' },
      { value: 'accepted', label: 'Acceptés' },
      { value: 'completed', label: 'Terminés' },
      { value: 'cancelled', label: 'Annulés' },
    ],
    users: [
      { value: 'all', label: 'Tous les rôles' },
      { value: 'patient', label: 'Patients' },
      { value: 'professional', label: 'Professionnels' },
      { value: 'facility', label: 'Établissements' },
      { value: 'agent', label: 'Agents' },
    ],
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="export-center">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <CardTitle className="text-lg">Export de données</CardTitle>
            <CardDescription>Exportez vos données en format CSV</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Export Type Selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {exportTypes.map((type) => {
            const Icon = type.icon;
            const isActive = activeExport === type.id;
            return (
              <button
                key={type.id}
                onClick={() => {
                  setActiveExport(type.id);
                  setFilters(f => ({ ...f, status: 'all', role: 'all' }));
                }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  isActive 
                    ? 'border-[#E53935] bg-red-50' 
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`export-type-${type.id}`}
              >
                <Icon className={`w-5 h-5 mb-2 ${isActive ? 'text-[#E53935]' : 'text-slate-400'}`} />
                <p className={`text-sm font-medium ${isActive ? 'text-[#E53935]' : 'text-slate-700'}`}>
                  {type.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl">
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Date de début</Label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
              className="h-10"
              data-testid="export-start-date"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Date de fin</Label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
              className="h-10"
              data-testid="export-end-date"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">
              {activeExport === 'users' ? 'Rôle' : 'Statut'}
            </Label>
            <Select 
              value={activeExport === 'users' ? filters.role : filters.status}
              onValueChange={(v) => setFilters(f => ({ 
                ...f, 
                [activeExport === 'users' ? 'role' : 'status']: v 
              }))}
            >
              <SelectTrigger className="h-10" data-testid="export-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(statusOptions[activeExport] || []).map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Export Button */}
        <div className="flex items-center justify-between">
          <div>
            {lastExport && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Dernier export: {lastExport.count} enregistrements</span>
              </div>
            )}
          </div>
          
          <Button
            onClick={handleExport}
            disabled={loading}
            className="bg-[#E53935] hover:bg-[#C62828]"
            data-testid="export-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter en CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExportCenter;
